import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { firestore, withActor, stripServerOwnedFields } from '../../backend'
import { collection, doc, updateDoc, query, where, getDoc, documentId, getDocs, setDoc } from 'firebase/firestore'
import { getVenues, getVenuesGroup } from './venuesReducer'
import { getSageSaveVenue, saveTiersToSage, toSageRejection } from '../../utils/sage'

/**
 * Enregistre un compte. Une fiche déjà envoyée vers Sage passe par le callable
 * `sage_saveTiers` (« Sage d'abord ») : un refus de Sage rejette le thunk avec
 * `{ code, params, message }` dans `action.payload` et rien n'est écrit.
 */
export const saveAccount = createAsyncThunk('accounts/saveAccount', async ({ id, data }, { getState, rejectWithValue }) => {
	// `metadata` / `sageSync` sont réservés au serveur : jamais réécrits depuis un brouillon.
	const payload = stripServerOwnedFields('accounts', data)
	try {
		if (id) {
			const state = getState()
			const sageVenue = getSageSaveVenue({ venues: getVenues(state), venuesGroup: getVenuesGroup(state), entity: data })
			if (sageVenue) return await saveTiersToSage({ venueId: sageVenue.id, accountId: id, data: payload })
			return await updateDoc(doc(firestore, 'accounts', id), withActor(payload))
		}
		const accountId = doc(collection(firestore, 'accounts')).id
		return await setDoc(doc(firestore, 'accounts', accountId), withActor(payload, { create: true }), { merge: true })
	} catch (error) {
		return rejectWithValue(toSageRejection(error))
	}
})

export const accountsSlice = createSlice({
	name: 'accounts',
	initialState: {
		value: [],
		status: 'idle',
		error: null,
		selectedAccount: {},
		isAccountDetailModalOpen: false,
	},
	reducers: {
		setAccountStatus: (state, action) => {
			state.status = action.payload
		},
		setSelectedAccount: (state, action) => {
			state.selectedAccount = action.payload
		},
		setIsAccountDetailModalOpen: (state, action) => {
			state.isAccountDetailModalOpen = action.payload
		},
	},
	extraReducers: {
		[saveAccount.pending]: (state, action) => {
			state.status = 'loading'
		},
		[saveAccount.fulfilled]: (state, action) => {
			state.status = 'succeeded'
		},
		[saveAccount.rejected]: (state, action) => {
			state.status = 'failed'
			// `rejectWithValue` porte { code, params, message } ; sinon l'erreur sérialisée
			state.error = action.payload || { code: action.error?.code, message: action.error?.message }
		},
	},
})

export const loadAccountFromId = async accountId => {
	const docSnap = await getDoc(doc(firestore, 'accounts', accountId))
	const { lastReservationDate, ...data } = docSnap.data()
	return { ...data, id: docSnap.id }
}

export const fetchAccountsByBatches = accountsIds => {
	const batchSize = 10
	const arrAccountsIds = []

	for (let i = 0; i < accountsIds.length; i += batchSize) {
		const batch = accountsIds.slice(i, i + batchSize)
		const promise = getDocs(query(collection(firestore, 'accounts'), where(documentId(), 'in', batch)))
		arrAccountsIds.push(promise)
	}

	return Promise.all(arrAccountsIds)
}

export const getNewAccountId = () => {
	return doc(collection(firestore, 'accounts')).id
}

export const updateAccount = (id, data) => {
	return updateDoc(doc(firestore, 'accounts', id), withActor(data))
}

export const getAccountStatus = state => state.accounts.status
export const getSelectedAccount = state => state.accounts.selectedAccount
export const getIsAccountDetailModalOpen = state => state.accounts.isAccountDetailModalOpen

export const { setAccountStatus, setSelectedAccount, setIsAccountDetailModalOpen } = accountsSlice.actions

export default accountsSlice.reducer
